import { CommonModule } from '@angular/common';
import { Component, Injector, OnInit, OnDestroy } from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import {DEBOUNCE_TIME, extractErrorMessage, goToNextPage} from '../../../../common/common';
import { AuthService } from '../../../../services/auth.service';
import { Resource } from '../../../../model/resources.model';
import {ApiService, CustomHttpParams} from 'src/app/services/api.service';
import { NgxPaginationModule } from 'ngx-pagination';
import { FormsModule } from '@angular/forms';
import {PaginationClass, PaginationModel} from "../../../../common/pagination";
import {LoaderComponent} from "../../../common/loader/loader.component";
import { BehaviorSubject, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-resource-index',
  standalone: true,
  imports: [RouterModule, CommonModule, NgxPaginationModule, FormsModule, LoaderComponent],
  templateUrl: './resource-index.component.html',
  styleUrl: './resource-index.component.scss'
})
export class ResourceIndexComponent implements OnInit, OnDestroy {
  // Search fields
  public idSearch: string = '';
  public resourceIdSearch: string = '';
  public registrationIdSearch: string = '';
  public eventsCountSearch: string = '';
  public vensCountSearch: string = '';
  public labelSearch: string = '';
  public updatedBySearch: string = '';

  // Debounce search using BehaviorSubject
  private searchParams$ = new BehaviorSubject<any>({
    id: '',
    resourceId: '',
    registrationId: '',
    eventsCount: '',
    vensCount: '',
    label: '',
    updatedBy: ''
  });
  private unsubscribe = new Subject<void>();

  protected readonly paginationId = 'resourcesPagination';
  private pagination = new PaginationClass(this.paginationId);
  protected paginationModel: PaginationModel = this.pagination.getPaginationModel();

  public isAdmin: boolean = false;
  public isLoading: boolean = false;
  public error: string | null = null;
  public displayedResources: Resource[] = [];
  public router: Router;

  constructor(private injector: Injector, private authService: AuthService, private resourcesServices: ApiService){
    this.router = injector.get(Router);
  }

  ngOnInit(): void {
    this.isAdmin = this.authService.checkAuthorization();
    this.paginationModel = this.pagination.resetPagination();

    // Setup debounced search that tracks actual parameter changes
    this.searchParams$.pipe(
      debounceTime(DEBOUNCE_TIME),
      distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)),
      takeUntil(this.unsubscribe)
    ).subscribe(() => {
      this.fetchResources();
    });
  }

  ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  buildSearchParams(): CustomHttpParams {
    const params: CustomHttpParams = {
      'depth': 2,
      'limit': this.pagination.itemsPerPage,
      'offset': this.pagination.offset,
    };

    // Add search parameters if they exist
    if (this.idSearch) {
      params['id'] = this.idSearch.trim();
    }
    if (this.resourceIdSearch) {
      params['resource_id'] = this.resourceIdSearch.trim();
    }
    if (this.registrationIdSearch) {
      params['program_unit'] = this.registrationIdSearch.trim();
    }
    if (this.labelSearch) {
      params['label'] = this.labelSearch.trim();
    }
    if (this.updatedBySearch) {
      params['updated_by'] = this.updatedBySearch.trim();
    }
    // Note: eventsCount and vensCount will be client-side filtered since they are computed values

    return params;
  }

  triggerSearch() {
    this.paginationModel = this.pagination.resetPagination();

    // Update the search parameters subject with current values
    this.searchParams$.next({
      id: this.idSearch,
      resourceId: this.resourceIdSearch,
      registrationId: this.registrationIdSearch,
      eventsCount: this.eventsCountSearch,
      vensCount: this.vensCountSearch,
      label: this.labelSearch,
      updatedBy: this.updatedBySearch
    });
  }

  fetchResources() {
    this.isLoading = true;
    this.error = null;

    // Build parameters using the centralized method
    const queryParams = this.buildSearchParams();

    this.resourcesServices.getResources(queryParams)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe({
        next: (response: any) => {
          this.displayedResources = response.map((resource: Resource) => resource);

          // Apply client-side filtering for computed fields
          this.displayedResources = this.applyClientSideFilters(this.displayedResources);

          this.paginationModel = this.pagination.updateTotalItems(this.displayedResources.length);
          this.isLoading = false;
        },
        error: (err) => {
          console.error('Error loading resource:', err);
          this.error = extractErrorMessage(err, "Failed to load resource.");
          this.isLoading = false;
        }
      });
  }

  private applyClientSideFilters(resources: Resource[]): Resource[] {
    let filteredResources = resources;

    // Filter by event count if search term exists
    if (this.eventsCountSearch) {
      const searchTerm = this.eventsCountSearch.trim();
      filteredResources = filteredResources.filter(resource =>
        resource.events && resource.events.length.toString().includes(searchTerm)
      );
    }

    // Filter by vens count if search term exists
    if (this.vensCountSearch) {
      const searchTerm = this.vensCountSearch.trim();
      filteredResources = filteredResources.filter(resource =>
        resource.vens && resource.vens.length.toString().includes(searchTerm)
      );
    }

    return filteredResources;
  }

  handlePageChange(newPage: number) {
    this.paginationModel = this.pagination.changePage(newPage);
    this.fetchResources();
  }

  nextPage(event: any, url: string, router: Router){
    return goToNextPage(event, url, router);
  }

  delete(resource: any) {
    if (confirm('Are you sure you want to delete this resource?')) {
      this.resourcesServices.deleteResources(resource.id)
        .pipe(takeUntil(this.unsubscribe))
        .subscribe({
          next: () => {
            this.fetchResources(); // Refresh list after deletion
          },
          error: (err) => {
            console.error('Error deleting resource:', err);
            this.error = extractErrorMessage(err, "Failed to delete resource.");
          }
        });
    }
  }
}
